import { toast } from 'vue-sonner';
import { List as CustomList } from '@/components/base/sonner';
import { h, markRaw } from 'vue';
import { i18n } from '@legacy/js/plugin/vue-i18n';

export type NotifyId = number | string;
export type NotifyTypes = 'info' | 'success' | 'warning' | 'error';
export type NotifyMessageArray = [string, object][] | string[];
export type NotifyMessages = string | NotifyMessageArray;
export type NotifyParams = object;
export type Notification = {
    message: NotifyMessages;
    params?: NotifyParams;
    settings?: NotificationSettings;
}
export type NotifyHandler = (message: NotifyMessages, params?: NotifyParams, settings?: NotificationSettings) => NotifyId | boolean;
export type NotifyVariants = Record<NotifyTypes, NotifyHandler>;


export interface NotificationSettings {
    type?: NotifyTypes;
    description?: string;
    list?: string[];
    duration?: number | typeof Infinity;
    saveInSession?: boolean;
}

export interface Notify {
    info: NotifyHandler;
    success: NotifyHandler;
    warning: NotifyHandler;
    error: NotifyHandler;
    plain: {
        info: NotifyHandler;
        success: NotifyHandler;
        warning: NotifyHandler;
        error: NotifyHandler;
    };
    hide: (id: NotifyId) => void;
    hideAll: () => void;
    promise: (promise: any, settings?: NotificationSettings) => ReturnType<typeof toast.promise>;
}

const DEFAULT_TITLES = {
    info: 'Hinweis',
    success: 'Erfolg',
    warning: 'Warnung',
    error: 'Fehler',
};

const DEFAULT_DURATIONS: Record<NotifyTypes, number> = {
    info: 2500,
    success: 2500,
    warning: 5000,
    error: 7500,
};

export const SESSION_STORAGE_KEY = 'sph.notifications';


export function translateMessages(message: NotifyMessages, params: NotifyParams = {}) {
    const list: NotifyMessageArray = Array.isArray(message) ? message : [[message, params]]
    return list.map((item) => {
        if (typeof item === 'string') {
            return i18n.t(item)
        }

        return i18n.t(item[0] || '', item[1] as any)
    })
}

function saveNotificationsInSession(message: NotifyMessages, params: NotifyParams = {}, settings?: NotificationSettings): boolean {
    try {
        const itemsRaw = sessionStorage.getItem(SESSION_STORAGE_KEY);
        let items = [];
        if (itemsRaw) {
            items = JSON.parse(itemsRaw);
        }

        items.push({ message, params, settings });
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(items));

        return true
    } catch (error) {
        console.error('Failed to save notifications in session:', error);
        return false
    }
}

function processSessionNotifications(notify: NotifyVariants): boolean {
    try {
        const itemsRaw = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (itemsRaw) {
            const items = JSON.parse(itemsRaw);

            items.forEach(({ message, params, settings }: { message: NotifyMessages; params: NotifyParams; settings?: NotificationSettings }, index: number) => {
                const type = settings?.type || 'info';

                setTimeout(() => {
                    notify[type](message, params, settings);
                }, 100 * index);
            });
        }

        sessionStorage.removeItem(SESSION_STORAGE_KEY);

        return true

    } catch (error) {
        console.error('Failed to process session notifications:', error);
        return false
    }
}

function notifyFactory(type: NotifyTypes, plain: boolean = false) {
    return (message: NotifyMessages, params: NotifyParams = {}, settings?: NotificationSettings) => {
        const processedSettings = { ...(settings || {}) as NotificationSettings };
        processedSettings.duration = processedSettings.duration ?? DEFAULT_DURATIONS[type];

        if (plain) {
            Object.assign(processedSettings, params);
        }

        // remove custom props from settings
        if (processedSettings.saveInSession) {
            delete processedSettings.saveInSession;
            return saveNotificationsInSession(message, params, { type, ...processedSettings })
        }

        let messageList: string[] = []
        if (plain) {
            messageList = Array.isArray(message)
                ? message.map((item) => (typeof item === 'string' ? item : item[0]))
                : [message];
        } else {
            messageList = translateMessages(message, params);
        }

        let list = processedSettings.list;
        delete processedSettings.list;

        const multiMessage = messageList.length > 1

        let title: string = ''
        let description: string | undefined = processedSettings?.description

        if (multiMessage) {
            title = i18n.t(DEFAULT_TITLES[type]);
            list = messageList;
        } else {
            title = messageList[0] ?? '';
        }

        const listForRender = list ?? [];
        const shouldRenderList = listForRender.length > 0;
        const ListComponent = shouldRenderList
            ? h(CustomList, { description: description ?? '', list: listForRender })
            : null;

        return toast[type](title, {
            ...(processedSettings || {}),
            description: shouldRenderList && ListComponent ? markRaw(ListComponent) : description
        }) as NotifyId ;
    }
}

export function useNotifications() {
    const variants = {
        info: notifyFactory('info'),
        success: notifyFactory('success'),
        warning: notifyFactory('warning'),
        error: notifyFactory('error'),
    }

    const variantsPlain = {
        info: notifyFactory('info', true),
        success: notifyFactory('success', true),
        warning: notifyFactory('warning', true),
        error: notifyFactory('error', true),
    }

    return {
        notify: {
            ...variants,
            plain: variantsPlain,

            hide: (id: NotifyId) => toast.dismiss(id),
            hideAll: () => toast.dismiss(),

            promise: (promise: any, settings?: NotificationSettings) => toast.promise(promise, settings),
        } satisfies Notify,

        processSessionNotifications: processSessionNotifications.bind(null, variants)
    }
}
